import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, LayoutDashboard, FolderHeart, MessageCircleHeart, School, LogOut, Users, Shield, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function LanguageToggle() {
  const { lang, setLanguage } = useLang();
  return (
    <div className="inline-flex items-center rounded-full border border-border bg-muted p-1" data-testid="language-toggle">
      {["en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLanguage(l)}
          data-testid={`lang-${l}`}
          aria-pressed={lang === l}
          className={cn(
            "px-3 py-1 text-sm font-semibold rounded-full transition-colors",
            lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const nav = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard },
    { to: "/activities", label: t("nav.myActivities"), icon: FolderHeart },
    { to: "/ask-brilla", label: t("nav.askBrilla"), icon: MessageCircleHeart },
    { to: "/profile", label: t("nav.profile"), icon: School },
    { to: "/pricing", label: t("nav.dashboard") === "Panel" ? "Planes" : "Plans", icon: CreditCard },
  ];
  if (user?.role === "coach" || user?.role === "admin") {
    nav.push({ to: "/coach", label: user.role === "admin" ? (t("nav.dashboard") === "Panel" ? "Entrenador" : "Coach") : (t("nav.dashboard") === "Panel" ? "Entrenador" : "Coach"), icon: Users });
  }
  if (user?.role === "admin") {
    nav.push({ to: "/admin", label: "Admin", icon: Shield });
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border no-print">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          <span className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-primary-foreground" strokeWidth={2} />
          </span>
          <span className="font-heading font-extrabold text-lg tracking-tight hidden sm:block">Brilla</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((n) => {
            const active = location.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors",
                  active ? "bg-muted text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                )}
              >
                <Icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageToggle />
          {user && (
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-heading font-bold text-sm" aria-hidden>
                {(user.name || "?").charAt(0).toUpperCase()}
              </span>
              <Button variant="ghost" size="icon" onClick={handleLogout} aria-label={t("common.logout")} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <nav className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto">
        {nav.map((n) => {
          const active = location.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} data-testid={`mnav-${n.to.replace("/", "")}`}
              className={cn("whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-semibold",
                active ? "bg-muted text-primary" : "text-muted-foreground")}>
              {n.label}
            </Link>
          );
        })}
      </nav>
    </header>
  );
}

export { LanguageToggle };
